/* eslint-disable max-len, react/forbid-prop-types, camelcase, prefer-template, jsx-a11y/no-static-element-interactions, react/sort-comp */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Grid, Button, Icon } from 'semantic-ui-react';
import LoadImage from '../../../components/LoadImage';
import { namespaces, TRANSLATION_FILES } from '../../../constants/translation';
import { serverRequestGetBlob } from '../../../actions/fetchM';
import GridDivider from '../../../components/UpdatedComponents/Basic/UCBGridDivider';
import ViewLarge from '../../../containers/modals/ViewLarge';
import COUNTRIES from '../../../constants/countries';
import INTL_FLAG_CODES from '../../../constants/intlFlagCodes';
import { CITIZENSHIP_FOREIGNER, crmContactCitizenship } from '../../../utils/vietnam';

class PurchaserSection extends Component {
  static propTypes = {
    contact: PropTypes.object,
    customerId: PropTypes.string,
    token: PropTypes.string,
    label: PropTypes.string,
    t: PropTypes.func,
  }

  static contextTypes = {
    store: PropTypes.object,
  }

  constructor(props) {
    super(props);
    this.mounted = false;
    this.imageRequestVersions = { front: 0, back: 0 };
    this.state = {
      icFrontBlob: null,
      icBackBlob: null,
      icFrontBlobRaw: null,
      icBackBlobRaw: null,
      fetchingFront: false,
      fetchingBack: false,
      showLarge: false,
      selectedSrc: null,
      selectedLabel: '',
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.handleFetchImages();
  }

  componentDidUpdate(prevProps) {
    const customerChanged = this.props.customerId !== prevProps.customerId;
    const tokenChanged = this.props.token !== prevProps.token;
    const currentContact = this.props.contact || {};
    const previousContact = prevProps.contact || {};
    const imageLinksChanged = currentContact.ic_front_link !== previousContact.ic_front_link
      || currentContact.ic_back_link !== previousContact.ic_back_link;
    if (customerChanged || tokenChanged || imageLinksChanged) {
      this.handleFetchImages();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.state.icFrontBlob) URL.revokeObjectURL(this.state.icFrontBlob);
    if (this.state.icBackBlob) URL.revokeObjectURL(this.state.icBackBlob);
  }

  handleFetchImages = () => {
    const { contact = {} } = this.props;
    // These links are supplied by Book API after it has checked access to the opportunity.
    // Do not rebuild the old customer-only URL here: it lacks the opportunity scope.
    this.fetchIdentityImage('front', contact.ic_front_link);
    this.fetchIdentityImage('back', contact.ic_back_link);
  }

  resetIdentityImage = (image, fetching = false) => {
    const stateKey = image === 'front' ? 'icFrontBlob' : 'icBackBlob';
    const flagKey = image === 'front' ? 'fetchingFront' : 'fetchingBack';
    const previousURL = this.state[stateKey];
    if (previousURL) URL.revokeObjectURL(previousURL);
    const nextState = {
      [stateKey]: null,
      [`${stateKey}Raw`]: null,
      [flagKey]: fetching,
    };
    if (this.state.selectedSrc === previousURL) {
      nextState.showLarge = false;
      nextState.selectedSrc = null;
    }
    this.setState(nextState);
  }

  fetchIdentityImage = async (image, imageURL) => {
    const { token } = this.props;
    const { store } = this.context;
    const stateKey = image === 'front' ? 'icFrontBlob' : 'icBackBlob';
    const flagKey = image === 'front' ? 'fetchingFront' : 'fetchingBack';
    const requestVersion = this.imageRequestVersions[image] + 1;
    this.imageRequestVersions[image] = requestVersion;

    if (!token || !imageURL) {
      this.resetIdentityImage(image);
      return;
    }

    this.resetIdentityImage(image, true);
    try {
      const blob = await serverRequestGetBlob(imageURL, store);
      if (this.mounted && this.imageRequestVersions[image] === requestVersion) {
        if (blob) {
          const url = URL.createObjectURL(blob);
          this.setState({ [stateKey]: url, [`${stateKey}Raw`]: blob });
        } else {
          this.setState({ [stateKey]: null, [`${stateKey}Raw`]: null });
        }
      }
    } catch (err) {
      if (this.mounted && this.imageRequestVersions[image] === requestVersion) {
        this.setState({ [stateKey]: null, [`${stateKey}Raw`]: null });
      }
    } finally {
      if (this.mounted && this.imageRequestVersions[image] === requestVersion) {
        this.setState({ [flagKey]: false });
      }
    }
  }

  handleViewImage = (src, label) => {
    this.setState({ showLarge: true, selectedSrc: src, selectedLabel: label });
  }

  handleCloseLarge = () => {
    this.setState({ showLarge: false, selectedSrc: null });
  }

  getDownloadExtension = (blob) => {
    const mimeType = ((blob && blob.type) || '').toLowerCase().split(';')[0].trim();
    const mimeTypeToExtension = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/bmp': 'bmp',
      'image/tiff': 'tiff',
      'application/pdf': 'pdf',
    };
    return mimeTypeToExtension[mimeType] || 'jpg';
  }

  handleDownloadAll = () => {
    const { icFrontBlob, icFrontBlobRaw, icBackBlob, icBackBlobRaw } = this.state;
    const files = [
      { url: icFrontBlob, blob: icFrontBlobRaw, name: 'CCCD_Front' },
      { url: icBackBlob, blob: icBackBlobRaw, name: 'CCCD_Back' },
    ];
    files.forEach((file) => {
      if (file.url && file.blob) {
        const extension = this.getDownloadExtension(file.blob);
        const link = document.createElement('a');
        link.href = file.url;
        link.download = `${file.name}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  }

  render() {
    const { t, contact = {}, label } = this.props;
    const {
      name = 'N/A',
      preferred_name = '',
      title = 'N/A',
      gender = 'N/A',
      date_of_birth = 'N/A',
      nationality_country_code = 'N/A',
      mobile = '',
      mobile_country_code = '',
      email = 'N/A',
      address_1 = 'N/A',
      address_2 = 'N/A',
      city: ward = 'N/A',
      postcode = 'N/A',
      state = 'N/A',
      country_code = '',
      cccd = '',
      passport = '',
      identity_type = '',
      other_identity = '',
      marital_status = 'N/A',
    } = contact;
    let identityLabel;
    let identityValue;
    switch (identity_type) {
      case '1':
        identityLabel = t('CCCD', { ns: TRANSLATION_FILES.screens });
        identityValue = cccd || 'N/A';
        break;
      case '2':
        identityLabel = t('Passport', { ns: TRANSLATION_FILES.screens });
        identityValue = passport || 'N/A';
        break;
      case '3':
        identityLabel = t('Enterprise Registration Number', { ns: TRANSLATION_FILES.screens });
        identityValue = other_identity || 'N/A';
        break;
      default:
        identityLabel = 'N/A';
        identityValue = 'N/A';
        break;
    }
    const formattedDate = date_of_birth && date_of_birth !== 'N/A'
      ? moment(date_of_birth).format('DD/MM/YYYY')
      : 'N/A';
    const country = COUNTRIES.find(item => item.countryCode === (country_code || '').toLowerCase()) || {};
    const nationality = COUNTRIES.find(item => item.countryCode === (nationality_country_code || '').toLowerCase()) || {};
    // CPI-1248: show the citizenship the CRM actually declared. This row used to be derived purely
    // from nationality, which made it disagree with the two Vietnam rules - both read the
    // declaration, not the passport. A Vietnamese-national purchaser declared `foreigner` read as
    // Local here while the rules refused them, and an undeclared purchaser with a foreign
    // nationality read as Foreigner while the quota did not count them at all. Nationality stays
    // as the fallback for rows that predate the field, which is what Sales > Purchaser already does.
    const declaredCitizenship = crmContactCitizenship(contact);
    const isForeigner = declaredCitizenship
      ? declaredCitizenship === CITIZENSHIP_FOREIGNER
      : nationality.countryCode !== 'vn';
    const citizenship = isForeigner
      ? t('Foreigner', { ns: TRANSLATION_FILES.translation, country: nationality.name || 'N/A' })
      : t('Local', { ns: TRANSLATION_FILES.translation });
    const countryMatch = INTL_FLAG_CODES.find(item => item.countryCode === (mobile_country_code || '').toLowerCase()) || {};
    const mobilePrefix = (countryMatch && countryMatch.value) ? '+' + countryMatch.value : '';

    return (
      <React.Fragment>
        {label && (
          <Grid.Row className="uc-grid-title-row">
            <Grid.Column width={16}>
              {label}
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row className="uc-grid-detail-row">
          <Grid.Column width={3}>
            {identityLabel}
          </Grid.Column>
          <Grid.Column width={5}>
            {identityValue}
          </Grid.Column>
          <Grid.Column width={3}>
            <span>{t('Title', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{t(title, { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className="uc-grid-detail-row">
          <Grid.Column width={3}>
            <span>{t('Full Name', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{name}{preferred_name ? ` (${preferred_name})` : ''}</span>
          </Grid.Column>
          <Grid.Column width={3}>
            <span>{t('Gender', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span style={{ textTransform: 'capitalize' }}>{t(gender.toLowerCase(), { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className="uc-grid-detail-row">
          <Grid.Column width={3}>
            <span>{t('Date of birth', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{formattedDate}</span>
          </Grid.Column>
          <Grid.Column width={3}>
            <span>{t('Nationality', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{nationality.name || 'N/A'}</span>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className="uc-grid-detail-row">
          <Grid.Column width={3}>
            <span>{t('Mobile no.', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{mobilePrefix} {mobile}</span>
          </Grid.Column>
          <Grid.Column width={3}>
            <span>{t('Email', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{email}</span>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className="uc-grid-detail-row">
          <Grid.Column width={3}>
            <span>{t('Marital Status', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span style={{ textTransform: 'capitalize' }}>{t(marital_status.toLowerCase(), { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
        </Grid.Row>

        <GridDivider />

        <Grid.Row className="uc-grid-detail-row">
          <Grid.Column width={3}>
            <span>{t('Address Line 1 (House No, Street)', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{address_1}</span>
          </Grid.Column>
          <Grid.Column width={3}>
            <span>{t('Address Line 2 (Street)', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{address_2}</span>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className="uc-grid-detail-row">
          <Grid.Column width={3}>
            <span>{t('Citizenship', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{citizenship}</span>
          </Grid.Column>
          <Grid.Column width={3}>
            <span>{t('Postal Code', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{postcode}</span>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className="uc-grid-detail-row">
          <Grid.Column width={3}>
            <span>{t('Ward', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{ward}</span>
          </Grid.Column>
          <Grid.Column width={3}>
            <span>{t('City / Province', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{state}</span>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className="uc-grid-detail-row">
          <Grid.Column width={3}>
            <span>{t('Country', { ns: TRANSLATION_FILES.screens })}</span>
          </Grid.Column>
          <Grid.Column width={5}>
            <span>{country.name || 'N/A'}</span>
          </Grid.Column>
        </Grid.Row>

        <GridDivider />

        <Grid.Row className="uc-grid-detail-row">
          <Grid.Column width={16}>
            <div className="cccd-download-button-container">
              <span>{t('Identity Snapshot', { ns: TRANSLATION_FILES.screens })}</span>
              {(this.state.icFrontBlob || this.state.icBackBlob) && (
                <Button className="cccd-download-button" onClick={this.handleDownloadAll}>
                  <Icon name="download" />
                  {t('Download All', { ns: TRANSLATION_FILES.screens })}
                </Button>
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={8}>
            <div className={`cccd-image-container ${!this.state.icFrontBlob ? 'is-empty' : ''}`} onClick={() => this.state.icFrontBlob && this.handleViewImage(this.state.icFrontBlob, t('CCCD Front'))}>
              <LoadImage loading={this.state.fetchingFront} src={this.state.icFrontBlob} size="medium" />
            </div>
          </Grid.Column>
          { identity_type === '1' && (
          <Grid.Column width={8}>
            <div className={`cccd-image-container ${!this.state.icBackBlob ? 'is-empty' : ''}`} onClick={() => this.state.icBackBlob && this.handleViewImage(this.state.icBackBlob, t('CCCD Back'))}>
              <LoadImage loading={this.state.fetchingBack} src={this.state.icBackBlob} size="medium" />
            </div>
          </Grid.Column>
          )}
        </Grid.Row>

        <ViewLarge
          open={this.state.showLarge}
          onClose={this.handleCloseLarge}
          src={this.state.selectedSrc}
          picture={{ label: this.state.selectedLabel }}
        />
      </React.Fragment>
    );
  }
}

export default withTranslation(namespaces)(PurchaserSection);



// WEBPACK FOOTER //
// ./src/containers/Opportunities/Overview/PurchaserSection.jsx